<template>
  <!-- ── Icon mode ─────────────────────────────────────────────────────── -->
  <!-- The robot's own pen mark (its own widget, card t_390bc438): the disc
       inside the status ring, working while the state says Running, and the
       ring carrying the job's progress — or the charge it is holding on the
       dock. -->
  <template v-if="mode === 'icon'">
    <div
      class="device-icon-pin"
      :class="[sizeClass, away ? 'device-active--vacuum' : '']"
      :style="{ '--pin-active-color': ACCENT }"
      :title="device.deviceName">
      <ControlStatusRing :value="ringValue" :accent="ACCENT">
        <SwitchAnimation class="robot-art robot-art--pin" kind="vacuum" :active="running" />
      </ControlStatusRing>
      <div v-if="running" class="device-icon-pin__glow"></div>
    </div>
  </template>

  <!-- ── Grid mode ─────────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'grid'">
    <DeviceGridCell
      :icon="device.icon || 'vacuum'"
      :name="displayName"
      :is-on="away"
      active-color="sky-400"
      @toggle="emit('open-control-modal')"
      @long-press="emit('open-control-modal')" />
  </template>

  <!-- ── Control mode ──────────────────────────────────────────────────── -->
  <!-- The sheet body is the family's own (shared with the mower): state chip,
       battery, mode selector, the verbs, progress and the error strip. The host
       owns the head and the panel; the body owns the hero and the rows. -->
  <template v-else-if="mode === 'control'">
    <RobotSheet :device="device" kind="vacuum" />
  </template>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import SwitchAnimation from './switch/SwitchAnimation.vue';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import RobotSheet from './robot/RobotSheet.vue';
import { useDeviceValues } from '../../composables/useDeviceValues';
import {
  ROBOT_BATTERY_KEY,
  ROBOT_PROGRESS_KEY,
  robotAway,
  robotDocked,
  robotPercent,
  robotRunning,
  robotState,
} from '../../utils/robotState';
import type { Device } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
}

const props = withDefaults(defineProps<Props>(), { mode: 'icon', size: 'md' });

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
}>();

/** The family's own accent (`--device-color-vacuum`, the teal in `style.css`). */
const ACCENT = 'var(--device-color-vacuum)';

// ── State ────────────────────────────────────────────────────────────────
const { currentValues, initializeValues } = useDeviceValues(props.device.id);

if (props.device.deviceValues?.length) initializeValues(props.device.deviceValues);
watch(
  () => props.device.deviceValues,
  (v) => {
    if (v?.length) initializeValues(v);
  },
  { deep: true },
);

const rows = computed(() => props.device.deviceValues ?? []);
const state = computed(() => robotState(rows.value, currentValues.value));
const running = computed(() => robotRunning(state.value));
const away = computed(() => robotAway(state.value));
const battery = computed(() => robotPercent(rows.value, currentValues.value, ROBOT_BATTERY_KEY));
const progress = computed(() => robotPercent(rows.value, currentValues.value, ROBOT_PROGRESS_KEY));

/**
 * What the pin's ring carries: the job's progress while the machine is out
 * working it, its charge while it rests — the one reading that answers "how is
 * it doing" in either phase. A battery the house has not reported reads as an
 * empty ring, never a guess.
 */
const ringValue = computed(() => {
  if (running.value) return progress.value;
  if (robotDocked(state.value)) return battery.value;
  return progress.value ?? battery.value;
});

// ── Display ──────────────────────────────────────────────────────────────
const displayName = computed(() =>
  props.displayMode === 'full' ? props.device.deviceName : props.device.shortName || props.device.deviceName,
);
const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));
</script>

<style scoped>
/* ── Active states ───────────────────────────────────────────────────────
   The vacuum family's accent is teal (`--device-color-vacuum`); the card's
   border, the name, the state line and the rail ride it while the machine is
   away from its dock. */
.robot-card--active {
  color: var(--device-color-vacuum);
  border-color: color-mix(in srgb, currentColor 55%, transparent) !important;
  box-shadow:
    0 0 10px color-mix(in srgb, currentColor 22%, transparent),
    0 0 26px color-mix(in srgb, currentColor 8%, transparent);
}

.robot-card--active .device-name,
.robot-card--active .robot-current-value {
  color: currentColor !important;
}

.robot-card-layout {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-height: 0;
  margin-top: 0;
}

.robot-current-value {
  font-size: var(--tile-status-size); /* the tiles' value line */
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
}

.robot-value-unit {
  margin-left: 0.35em;
  font-size: 0.75em;
  font-weight: 600;
  color: rgb(var(--gray-500));
}

/* The job's rail on the card — the tiles' trough, sized by `progress`. */
.robot-bar {
  position: relative;
  width: 100%;
  height: 1.35em;
  margin-top: 0.5em;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(148, 163, 184, 0.18);
}

.robot-bar__fill {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: var(--device-color-vacuum);
  transition: width 0.15s ease-out;
}

/* ── The robot drawing ───────────────────────────────────────────────────
   `SwitchAnimation` paints in `currentColor`: the resting state takes the
   tile's muted ink, the working one the family's accent. */
.robot-art {
  width: 100%;
  height: 100%;
  color: color-mix(in srgb, currentColor 78%, transparent);
  transition: color 0.3s ease;
}

/* Inside the icon pin the drawing sits at the glyph's own ratio */
.robot-art--pin {
  width: 62%;
  height: 62%;
}
</style>
